import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';
import { t } from '../i18n/i18n';
import {
  getScreeningCustomSteps,
  getScreeningCustomStepById,
  createScreeningCustomStep,
  updateScreeningCustomStep,
  deleteScreeningCustomStep,
  bulkCreateScreeningCustomSteps,
  bulkDeleteScreeningCustomSteps,
  getScreeningCustomStepByPriority,
} from '../API/services/screeningCustomStepsService';

const QUERY_KEYS = {
  CUSTOM_STEPS: 'screeningCustomSteps',
  CUSTOM_STEP: 'screeningCustomStep',
  CUSTOM_STEP_PRIORITY: 'screeningCustomStepPriority',
};

// Hook to fetch all screening custom steps
export const useScreeningCustomSteps = (params = {}, options = {}) =>
  useQuery({
    queryKey: [QUERY_KEYS.CUSTOM_STEPS, params],
    queryFn: () => getScreeningCustomSteps(params),
    retry: (failureCount, error) => {
      if (error?.response?.status === 403 || error?.response?.status === 401) {
        return false;
      }
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });

// Hook to fetch a single screening custom step by ID
export const useScreeningCustomStep = (id, options = {}) =>
  useQuery({
    queryKey: [QUERY_KEYS.CUSTOM_STEP, id],
    queryFn: () => getScreeningCustomStepById(id),
    enabled: !!id,
    retry: (failureCount, error) => {
      if (error?.response?.status === 403 || error?.response?.status === 401) {
        return false;
      }
      return failureCount < 3;
    },
    ...options,
  });

// Hook to fetch custom step by priority
export const useScreeningCustomStepByPriority = (
  step,
  workspaceId,
  screeningId,
  testGroupGrouperKey,
  options = {}
) =>
  useQuery({
    queryKey: [
      QUERY_KEYS.CUSTOM_STEP_PRIORITY,
      { step, workspaceId, screeningId, testGroupGrouperKey },
    ],
    queryFn: () =>
      getScreeningCustomStepByPriority(step, workspaceId, screeningId, testGroupGrouperKey),
    enabled: !!step && !!workspaceId,
    retry: (failureCount, error) => {
      if (error?.response?.status === 403 || error?.response?.status === 401) {
        return false;
      }
      return failureCount < 1;
    },
    ...options,
  });

// Hook to create a screening custom step
export const useCreateScreeningCustomStep = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createScreeningCustomStep,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOM_STEPS] });
      notification.success({
        message: t('SCREENING_CUSTOM_STEP_CREATED'),
        description: t('SCREENING_CUSTOM_STEP_CREATED_DESC'),
      });
    },
    onError: (error) => {
      notification.error({
        message: t('SCREENING_CUSTOM_STEP_CREATE_ERROR'),
        description: error.message,
      });
    },
  });
};

// Hook to update a screening custom step
export const useUpdateScreeningCustomStep = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, customStep }) => updateScreeningCustomStep(id, customStep),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOM_STEPS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOM_STEP, variables.id] });
      notification.success({
        message: t('SCREENING_CUSTOM_STEP_UPDATED'),
        description: t('SCREENING_CUSTOM_STEP_UPDATED_DESC'),
      });
    },
    onError: (error) => {
      notification.error({
        message: t('SCREENING_CUSTOM_STEP_UPDATE_ERROR'),
        description: error.message,
      });
    },
  });
};

// Hook to delete a screening custom step
export const useDeleteScreeningCustomStep = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteScreeningCustomStep,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOM_STEPS] });
      queryClient.removeQueries({ queryKey: [QUERY_KEYS.CUSTOM_STEP, id] });
      notification.success({
        message: t('SCREENING_CUSTOM_STEP_DELETED'),
        description: t('SCREENING_CUSTOM_STEP_DELETED_DESC'),
      });
    },
    onError: (error) => {
      notification.error({
        message: t('SCREENING_CUSTOM_STEP_DELETE_ERROR'),
        description: error.message,
      });
    },
  });
};

// Hook for bulk create
export const useBulkCreateScreeningCustomSteps = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bulkCreateScreeningCustomSteps,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOM_STEPS] });
      notification.success({
        message: t('SCREENING_CUSTOM_STEPS_BULK_CREATED'),
        description: t('SCREENING_CUSTOM_STEPS_BULK_CREATED_DESC'),
      });
    },
    onError: (error) => {
      notification.error({
        message: t('SCREENING_CUSTOM_STEPS_BULK_CREATE_ERROR'),
        description: error.message,
      });
    },
  });
};

// Hook for bulk delete
export const useBulkDeleteScreeningCustomSteps = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bulkDeleteScreeningCustomSteps,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOM_STEPS] });
      notification.success({
        message: t('SCREENING_CUSTOM_STEPS_BULK_DELETED'),
        description: t('SCREENING_CUSTOM_STEPS_BULK_DELETED_DESC'),
      });
    },
    onError: (error) => {
      notification.error({
        message: t('SCREENING_CUSTOM_STEPS_BULK_DELETE_ERROR'),
        description: error.message,
      });
    },
  });
};
